"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useGroupStore } from "@/store/groupStore";
import { CreateGroupModal, CreateVolunteerModal, CreateEventModal, CreateNewsModal, CreateProviderModal, CreateBlogModal, CreateResourceModal, CreateTeamBioModal, CreateProgramCardModal, CreatePartnersModal, Toast } from "@/components/ui";
import { Plus, Users, Heart, Calendar, FileText, Stethoscope, PenTool, BookOpen, UserCircle, Briefcase, Image as ImageIcon } from "lucide-react";
import { ActivityLogger } from "@/lib/activityLog";
import { canCreateGroupsForUser } from "@/utils";

export default function CreateButton() {
  const router = useRouter();
  const { currentUser, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { canUserCreateOpportunity, createGroup, fetchMyMemberships } = useGroupStore();
  const [isOpen, setIsOpen] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showVolunteerModal, setShowVolunteerModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showTeamBioModal, setShowTeamBioModal] = useState(false);
  const [showProgramCardModal, setShowProgramCardModal] = useState(false);
  const [showPartnersModal, setShowPartnersModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isAdmin = currentUser?.role?.type === 'admin' || currentUser?.role?.type === 'superadmin';
  const canCreateBlog = currentUser?.role?.type === 'blog_writer' || isAdmin;
  const isAtLeast16 = canCreateGroupsForUser(currentUser);
  const canPostOpportunity = currentUser && (isAdmin || (isAtLeast16 && canUserCreateOpportunity(currentUser.id)));
  const isProviderVerified = currentUser?.providerVerified === true;
  const canCreateGroup = canCreateGroupsForUser(currentUser);
  const hasAnyCreateOption = isAdmin || Boolean(canPostOpportunity) || canCreateGroup || isProviderVerified || canCreateBlog;

  useEffect(() => {
    if (currentUser) fetchMyMemberships(currentUser.id);
  }, [currentUser, fetchMyMemberships]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleVolunteerResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 4000);
  };

  const handleEventResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 4000);
  };

  const handleNewsResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 4000);
  };

  const handleProviderResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 4000);
  };

  const handleBlogResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 4000);
  };

  const handleResourceResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 4000);
  };

  const handleTeamBioResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 4000);
  };

  const handleProgramCardResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 4000);
  };

  const handlePartnersResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 4000);
  };

  const handleCreateGroup = async (data: { name: string; description: string; rulesText: string; categorySlugs: string[]; acceptingMembers: boolean; memberListVisible: boolean; eventListVisible: boolean; memberApprovalMode: 'auto' | 'manual'; image: File | null; images?: File[]; avatar?: File | null }) => {
    if (!currentUser) return { success: false };
    const result = await createGroup(data, currentUser.id);
    setShowGroupModal(false);
    if (result.success && result.groupDocumentId) {
      ActivityLogger.createGroup(currentUser, data.name, result.groupDocumentId);
      router.push(`/groups/${result.groupDocumentId}`);
    } else {
      setToast({ message: result.error || 'Failed to create group', type: 'error' });
      setTimeout(() => setToast(null), 4000);
    }
    return result;
  };

  if (!isAuthenticated || !hasAnyCreateOption) return null;

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 bg-action-green text-black px-3 py-1.5 rounded-md text-sm font-medium hover:opacity-90 transition"
          data-testid="create-btn-trigger"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">{t.create.create}</span>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-background border border-border rounded-lg shadow-lg z-50 overflow-hidden" data-testid="create-btn-dropdown">
            <div className="py-1">
              {canPostOpportunity && (
                <button
                  onClick={() => { setIsOpen(false); setShowNewsModal(true); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-sidebar-hover transition"
                  data-testid="create-btn-news"
                >
                  <FileText size={18} className="text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{t.create.post}</p>
                    <p className="text-xs text-caption">{t.create.postDesc}</p>
                  </div>
                </button>
              )}
              {canPostOpportunity && (
                <button
                  onClick={() => { setIsOpen(false); setShowEventModal(true); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-sidebar-hover transition"
                  data-testid="create-btn-event"
                >
                  <Calendar size={18} className="text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{t.create.event}</p>
                    <p className="text-xs text-caption">{t.create.eventDesc}</p>
                  </div>
                </button>
              )}
              {canCreateGroup && (
                <button
                  onClick={() => { setIsOpen(false); setShowGroupModal(true); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-sidebar-hover transition"
                  data-testid="create-btn-group"
                >
                  <Users size={18} className="text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{t.create.group}</p>
                    <p className="text-xs text-caption">{t.create.groupDesc}</p>
                  </div>
                </button>
              )}
              {isProviderVerified && (
                <button
                  onClick={() => { setIsOpen(false); setShowProviderModal(true); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-sidebar-hover transition"
                  data-testid="create-btn-provider"
                >
                  <Stethoscope size={18} className="text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{t.create.provider}</p>
                    <p className="text-xs text-caption">{t.create.providerDesc}</p>
                  </div>
                </button>
              )}
              {canCreateBlog && (
                <button
                  onClick={() => { setIsOpen(false); setShowBlogModal(true); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-sidebar-hover transition"
                  data-testid="create-btn-blog"
                >
                  <PenTool size={18} className="text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{t.create.blog}</p>
                    <p className="text-xs text-caption">{t.create.blogDesc}</p>
                  </div>
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => { setIsOpen(false); setShowResourceModal(true); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-sidebar-hover transition"
                  data-testid="create-btn-resource"
                >
                  <BookOpen size={18} className="text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{t.create.resource}</p>
                    <p className="text-xs text-caption">{t.create.resourceDesc}</p>
                  </div>
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => { setIsOpen(false); setShowTeamBioModal(true); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-sidebar-hover transition"
                  data-testid="create-btn-team-bio"
                >
                  <UserCircle size={18} className="text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{t.create.teamBio}</p>
                    <p className="text-xs text-caption">{t.create.teamBioDesc}</p>
                  </div>
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => { setIsOpen(false); setShowProgramCardModal(true); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-sidebar-hover transition"
                  data-testid="create-btn-program-card"
                >
                  <Briefcase size={18} className="text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{t.create.programCard}</p>
                    <p className="text-xs text-caption">{t.create.programCardDesc}</p>
                  </div>
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => { setIsOpen(false); setShowPartnersModal(true); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-sidebar-hover transition"
                  data-testid="create-btn-partners"
                >
                  <ImageIcon size={18} className="text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{t.create.partnersSection}</p>
                    <p className="text-xs text-caption">{t.create.partnersSectionDesc}</p>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <CreateNewsModal
        isOpen={showNewsModal}
        onClose={() => setShowNewsModal(false)}
        onResult={handleNewsResult}
      />

      <CreateEventModal
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        onResult={handleEventResult}
        onCreated={(documentId) => router.push(`/events/${documentId}`)}
      />

      <CreateGroupModal
        isOpen={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        onCreate={handleCreateGroup}
      />

      <CreateVolunteerModal
        isOpen={showVolunteerModal}
        onClose={() => setShowVolunteerModal(false)}
        onResult={handleVolunteerResult}
      />

      <CreateProviderModal
        isOpen={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        onResult={handleProviderResult}
      />

      <CreateBlogModal
        isOpen={showBlogModal}
        onClose={() => setShowBlogModal(false)}
        onResult={handleBlogResult}
      />

      <CreateResourceModal
        isOpen={showResourceModal}
        onClose={() => setShowResourceModal(false)}
        onResult={handleResourceResult}
      />

      <CreateTeamBioModal
        isOpen={showTeamBioModal}
        onClose={() => setShowTeamBioModal(false)}
        onResult={handleTeamBioResult}
      />

      <CreateProgramCardModal
        isOpen={showProgramCardModal}
        onClose={() => setShowProgramCardModal(false)}
        onResult={handleProgramCardResult}
      />

      <CreatePartnersModal
        isOpen={showPartnersModal}
        onClose={() => setShowPartnersModal(false)}
        onResult={handlePartnersResult}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

